import React from 'react';
import { api } from '../api/client';
import { COLORS, Banner, Spinner, dateTime } from '../components/ui';
import StandupScreen from '../components/rm/StandupScreen';
import PeopleScreen from '../components/rm/PeopleScreen';
import MonthlyScreen from '../components/rm/MonthlyScreen';
import WoDrawer from '../components/rm/WoDrawer';

/**
 * R&M work order dashboard.
 *
 * Three screens over one fact set: the 8:00 stand-up, the people scorecard and
 * the monthly review. The period and filter controls are global -- they mean
 * the same thing on every screen -- and each screen defaults to the period it
 * is actually read over.
 */

const SCREENS = [
    { key: 'standup', label: 'Stand-up', defaultPeriod: '24h' },
    { key: 'people', label: 'Coordinators', defaultPeriod: '7d' },
    { key: 'monthly', label: 'Monthly', defaultPeriod: 'month' },
];

const PERIODS = [
    { key: '24h', label: 'Last 24 hours' },
    { key: '7d', label: 'Last 7 days' },
    { key: '14d', label: 'Last 14 days' },
    { key: '30d', label: 'Last 30 days' },
    { key: 'month', label: 'This month' },
];

/** Data older than this is called out rather than presented as current. */
const STALE_DATA_HOURS = 2;

/**
 * Query params, dropping anything unset. "All" is the stream filter's own
 * neutral value and means the same as absent.
 *
 * Returns a plain object rather than a string: every call goes through
 * `api.*`, which attaches the Entra bearer token. A raw fetch() gets a 401 in
 * production, where AUTH_MODE is strict.
 */
function params(values) {
    const out = {};
    for (const [k, v] of Object.entries(values)) {
        if (v != null && v !== '' && v !== 'All') out[k] = v;
    }
    return out;
}

const pill = (active) => ({
    fontSize: 12, padding: '4px 11px', borderRadius: 12, cursor: 'pointer',
    border: `1px solid ${active ? COLORS.navy : COLORS.axis}`,
    background: active ? COLORS.navy : COLORS.surface,
    color: active ? '#fff' : COLORS.ink2,
    fontWeight: active ? 600 : 400,
});

const select = {
    fontSize: 12, padding: '4px 6px', borderRadius: 12, maxWidth: 170,
    border: `1px solid ${COLORS.axis}`, background: COLORS.surface, color: COLORS.ink2,
};

function Filters({ value, onChange, lookups, onClear }) {
    const set = (key) => (e) => onChange({ ...value, [key]: e.target.value || null });
    const active = Object.entries(value).filter(([, v]) => v).length;

    return (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <select value={value.stream || 'All'} onChange={set('stream')} style={select}>
                {(lookups.streams || ['All']).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={value.state || ''} onChange={set('state')} style={select}>
                <option value="">All states</option>
                {(lookups.states || []).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={value.person || ''} onChange={set('person')} style={select}>
                <option value="">All people</option>
                {(lookups.people || []).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={value.contractor || ''} onChange={set('contractor')} style={select}>
                <option value="">All contractors</option>
                {(lookups.contractors || []).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={value.type || ''} onChange={set('type')} style={select}>
                <option value="">All types</option>
                {(lookups.types || []).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {active > 0 && (
                <button onClick={onClear} style={{
                    fontSize: 12, padding: '4px 10px', borderRadius: 12, cursor: 'pointer',
                    border: `1px solid ${COLORS.axis}`, background: COLORS.plane, color: COLORS.ink2,
                }}>Clear {active}</button>
            )}
        </div>
    );
}

/** Always-visible data freshness, per the brief -- never an implied "live". */
function AsAt({ asAt, meta }) {
    if (!asAt) return null;
    const ageHours = (Date.now() - new Date(asAt).getTime()) / 3600000;
    const stale = ageHours > STALE_DATA_HOURS;
    return (
        <div style={{ fontSize: 11.5, color: stale ? '#8a6100' : COLORS.muted, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{
                width: 7, height: 7, borderRadius: '50%',
                background: stale ? COLORS.warning : COLORS.good, display: 'inline-block',
            }} />
            Data as at {dateTime(asAt)}
            {stale && ` — ${Math.round(ageHours)}h old`}
            {meta?.activeCount != null && ` · ${meta.activeCount.toLocaleString()} active work orders`}
        </div>
    );
}

export default function RmDashboardPage() {
    const [screen, setScreen] = React.useState('standup');
    const [period, setPeriod] = React.useState('24h');
    const [filters, setFilters] = React.useState({ stream: null, state: null, person: null, contractor: null, type: null });
    const [lookups, setLookups] = React.useState({});
    const [data, setData] = React.useState(null);
    const [error, setError] = React.useState(null);
    const [loading, setLoading] = React.useState(true);

    const [drawer, setDrawer] = React.useState({ open: false, title: '', loading: false, items: [], total: 0 });

    /**
     * Dark, for this page only.
     *
     * Set on <body> rather than on a wrapper for two reasons: the page sits in a
     * fixed-width container, so a wrapper background would leave the rest of the
     * viewport light; and the detail drawer is position:fixed, so it needs to
     * inherit the palette from the body to be themed at all. Cleared on unmount
     * so navigating to PM compliance or Search returns to light.
     */
    React.useEffect(() => {
        const previous = document.body.dataset.rmTheme;
        document.body.dataset.rmTheme = 'dark';
        return () => {
            if (previous) document.body.dataset.rmTheme = previous;
            else delete document.body.dataset.rmTheme;
        };
    }, []);

    React.useEffect(() => {
        api.rmLookups().then(setLookups).catch(() => setLookups({}));
    }, []);

    // Serialised so the effect below re-runs on a value change, not on the new
    // object identity a filter render produces.
    const query = React.useMemo(
        () => JSON.stringify(params({ period, ...filters })), [period, filters]);

    React.useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        api.rmScreen(screen, JSON.parse(query))
            .then(d => { if (!cancelled) { setData(d); setLoading(false); } })
            .catch(e => { if (!cancelled) { setError(e.message); setLoading(false); } });
        return () => { cancelled = true; };
    }, [screen, query]);

    const pickScreen = (s) => {
        setScreen(s.key);
        setPeriod(s.defaultPeriod);
    };

    const openList = React.useCallback(async (set, title, extra = {}) => {
        setDrawer({ open: true, title, loading: true, items: [], total: 0 });
        try {
            const d = await api.rmWorkOrders(params({ set, period, ...filters, ...extra }));
            setDrawer({
                open: true, title, loading: false,
                items: d.items, total: d.total, truncated: d.truncated,
                subtitle: `${d.total.toLocaleString()} work order${d.total === 1 ? '' : 's'}`,
            });
        } catch (e) {
            setDrawer({ open: true, title, loading: false, items: [], total: 0, subtitle: e.message });
        }
    }, [period, filters]);

    /**
     * Export builds a CSV in the browser from what is already loaded, so it
     * matches the list on screen exactly and costs no extra query.
     */
    const exportDrawer = React.useCallback(() => {
        const rows = drawer.items || [];
        if (!rows.length) return;
        const cols = ['id', 'shopId', 'site', 'state', 'stream', 'status', 'subStatus', 'type',
            'priority', 'contractor', 'person', 'requestedDate', 'issuedDate', 'completeDate',
            'closedDate', 'dwellDays', 'ageDays', 'stage'];
        const esc = v => {
            if (v == null) return '';
            const s = String(v);
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };
        const csv = [cols.join(','), ...rows.map(r => cols.map(c => esc(r[c])).join(','))].join('\r\n');
        const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `rm-${drawer.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    }, [drawer]);

    const meta = data?.meta;

    return (
        <div>
            <header style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginBottom: 14 }}>
                <h1 style={{ margin: 0, fontSize: 20 }}>R&amp;M work orders</h1>
                <div style={{ display: 'flex', gap: 4 }}>
                    {SCREENS.map(s => (
                        <button key={s.key} onClick={() => pickScreen(s)} style={pill(screen === s.key)}>
                            {s.label}
                        </button>
                    ))}
                </div>
                <div style={{ marginLeft: 'auto' }}>
                    <AsAt asAt={meta?.asAt} meta={meta} />
                </div>
            </header>

            <div style={{
                display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center',
                background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                borderRadius: 8, padding: '9px 12px', marginBottom: 18,
            }}>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {PERIODS.map(p => (
                        <button key={p.key} onClick={() => setPeriod(p.key)} style={pill(period === p.key)}>
                            {p.label}
                        </button>
                    ))}
                </div>
                <div style={{ width: 1, height: 20, background: COLORS.grid }} />
                <Filters
                    value={filters}
                    lookups={lookups}
                    onChange={setFilters}
                    onClear={() => setFilters({ stream: null, state: null, person: null, contractor: null, type: null })}
                />
            </div>

            {error && (
                <Banner tone="danger">
                    <strong>Could not load the dashboard.</strong> {error}
                    {meta?.asAt && ` Last good data was ${dateTime(meta.asAt)}.`}
                </Banner>
            )}

            {loading && !data && <Spinner label="Loading work orders" />}

            {data && !error && (
                <>
                    {loading && <div style={{ fontSize: 12, color: COLORS.muted, marginBottom: 8 }}>Refreshing…</div>}
                    {screen === 'standup' && <StandupScreen data={data} onOpenList={openList} />}
                    {screen === 'people' && (
                        <PeopleScreen
                            data={data}
                            onOpenList={openList}
                            onPickPerson={person => setFilters(f => ({ ...f, person }))}
                        />
                    )}
                    {screen === 'monthly' && <MonthlyScreen data={data} onOpenList={openList} />}
                </>
            )}

            <WoDrawer
                {...drawer}
                onClose={() => setDrawer(d => ({ ...d, open: false }))}
                onExport={exportDrawer}
            />
        </div>
    );
}
